import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Component, Inject, OnInit} from '@angular/core';
import {WorkbenchViewComponent} from '../../../shared/components/viewcontainers/workbench-view.component';
import {ViewConfigPayload, WORKBENCH_VIEW_CONFIG, WorkbenchContext, WorkbenchViewConfig} from '../../../shared/types';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import {ProjectId} from '../../../../report/report_types';
import {SearchService} from '@webmate-web-core/shared/services/search.service';
import {SearchResult, SearchResults} from '@webmate-web-core/shared/types/searchresult';
import {faExternalLink} from '@fortawesome/pro-solid-svg-icons';
import {WorkbenchCommandRegistry} from "../../../shared/services/workbench-command-registry.service";
import {of, Subject} from 'rxjs';
import {debounceTime, delay, switchMap, take, tap} from 'rxjs/operators';
import {DeviceId, MediaBrowserDataType} from '@webmate-web-core/shared/types/ids';
import {ImageService} from '@webmate-web-core/shared/services/image.service';
import {VideoRecordingService} from '@webmate-web-core/shared/services/video-recording.service';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {Device} from '@webmate-web-core/shared/types/devices/device';
import moment from 'moment';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {
    CreateOrDeleteImagePermission,
    RecordVideosPermission
} from '@webmate-web-core/shared/constants/permission.constants';
import {WorkbenchConsoleService} from '../../../shared/services/workbench-console.service';

export interface DataRepositoryViewPayload extends ViewConfigPayload {
    data: {
        type: 'screenshot' | 'video';
        mode: 'normal' | 'selection';
    },
}

@UntilDestroy()
@Component({
    templateUrl: './data-repository.component.html',
    styleUrls: ['./data-repository.component.less']
})
export class DataRepositoryViewComponent extends WorkbenchViewComponent implements OnInit{

    workbenchContext: WorkbenchContext;
    projectId: ProjectId = undefined;
    deviceId: DeviceId = undefined;
    device: Device;
    resourceType: MediaBrowserDataType;
    query: string = '';
    result?: SearchResults;

    currentPage: number = 0;
    rows: number = 8;
    firstObject: number = 0;
    totalItems: number = 0;
    private searchSubject = new Subject<string>();

    uploading: boolean = false;
    fileNotAccepted: boolean = false;
    searchFailed: boolean = false;

    hasVideoUploadPermission: boolean = false;
    hasImageUploadPermission: boolean = false;

    validImageTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    validVideoTypes = ['video/mp4', 'video/webm'];

    animateSelectButton: boolean = false;

    constructor(layoutManager: WorkbenchLayoutManager,
            @Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<DataRepositoryViewPayload>,
            private readonly workbenchContextService: WorkbenchContextService,
            private searchService: SearchService,
            public imageService: ImageService,
            private readonly workbenchCommandRegistry: WorkbenchCommandRegistry,
            private userService: UserService,
            public readonly videoRecordingService: VideoRecordingService,
            private deviceService: DeviceService,
            private workbenchConsoleService: WorkbenchConsoleService
    ){
        super(layoutManager, config);
    }

    ngOnInit() {
        this.projectId = this.workbenchContextService.getProjectId();
        this.resourceType = this.config.payload.data.type;
        this.workbenchContextService.subscribeWorkbenchContext$().pipe(untilDestroyed(this),
            switchMap(context => {
                this.deviceId = context.deviceContext?.activeDevice;
                if (this.deviceId){
                    return this.deviceService.getDevice$(this.deviceId);
                } else {
                    return of(undefined);
                }
            }),
            tap(device => {
                this.device = device;
            }),
            switchMap(() => {
                return this.getResults$();
            }),
            tap(results => {
                if (results){
                    this.processResults(results);
                }
            }),
            switchMap(() =>
                this.searchSubject.pipe(
                    debounceTime(500),
                    switchMap(() => {
                        return this.getResults$();
                    }),
                    tap(results => {
                        if (results){
                            this.processResults(results);
                        }
                    })
                )
            )
        ).subscribe();

        this.workbenchContextService.subscribeWorkbenchContext$().pipe(
            untilDestroyed(this),
            switchMap(context => {
                const activeDeviceId = context.deviceContext?.activeDevice;
                if (!activeDeviceId) {
                    return of(undefined);
                }
                return this.workbenchConsoleService.getWorkbenchConsoleEvent$(activeDeviceId);
            }),
            tap(event => {
                if (event?.type === 'mediaBrowserImageSelection') {
                    this.animateSelectButton = true;
                }
            })
        ).subscribe();

        this.userService.hasPermissionInProject(this.projectId, CreateOrDeleteImagePermission).pipe(
            switchMap(imagePermission => {
                this.hasImageUploadPermission = imagePermission;
                return this.userService.hasPermissionInProject(this.projectId, RecordVideosPermission);
            }),
            tap(videoPermission => {
                this.hasVideoUploadPermission = videoPermission;
            })
        ).subscribe();
        if (this.projectId) {
            this.searchService.getDeletedNotification(this.projectId).pipe(
                untilDestroyed(this),
                switchMap(id => {
                    if (!!this.result && this.result.results.find(f => f.value.id === id)) {
                        return this.getResults$();
                    } else {
                        return of(undefined);
                    }
                }),
                tap(results => {
                    if (results) {
                        this.processResults(results);
                    }
                })
            ).subscribe();
            this.searchService.getIndexingNotifications(this.projectId).pipe(
                untilDestroyed(this),
                delay(1000), //The notifications are received some time before the image is available
                switchMap(() => {
                    return this.getResults$();
                }),
                tap(results => {
                    if (results) {
                        this.processResults(results);
                    }
                })
            ).subscribe();
        }
    }

    onSearch() {
        this.searchSubject.next(this.query);
    }

    openScreenshot(r: SearchResult) {
        this.workbenchCommandRegistry.executeCommand('workbench-tools:openScreenshot', {id: r.value.id, name: r.value.name});
    }

    openVideo(r: SearchResult) {
        this.workbenchCommandRegistry.executeCommand('workbench-tools:openVideo', {
            id: r.value.id,
            name: r.value.name,
            duration: r.value.data?.duration,
            size: r.value.data?.filesize,
            format: r.value.data?.format
        });
    }

    millisToMinutesAndSeconds(ms: string) {
        return moment.utc(Number(ms)).format('mm:ss');
    }

    getResults$() {
        if (this.query === '' || this.query.length >= 3) {
            return this.searchService.simpleSearch(this.projectId, this.query, this.currentPage * this.rows, this.rows, this.resourceType);
        } else {
            return of(undefined);
        }
    }

    processResults(result: SearchResults) {
        if (result) {
            this.searchFailed = false;
            this.uploading = false;
            this.result = result;
            this.totalItems = result.totalResults;
        } else {
            this.searchFailed = true;
        }
    }

    onPageChange($event) {
        this.firstObject = $event.first;
        this.currentPage = $event.page;
        this.rows = $event.rows;
        this.getResults$().subscribe(results => {
            if (results) {
                this.processResults(results);
            }
        });
    }

    dropFiles(event: File[]) {
        event.forEach(f => {
            if (this.validImageTypes.includes(f.type) && this.resourceType === "screenshot" && this.hasImageUploadPermission) {
                this.storeImage(f);
            } else if (this.validVideoTypes.includes(f.type) && this.resourceType === "video" && this.hasVideoUploadPermission) {
                this.storeVideo(f);
            } else {
                this.fileNotAccepted = true;
                this.uploading = false;
                setTimeout(() => this.fileNotAccepted = false, 3000); //The message should only be displayed for 3s
            }
        });
    }

    selectFiles(event: Event) {
        const target = event.target as HTMLInputElement;
        const files = target.files as FileList;
        Array.from(files).forEach(f => {
            if (this.validImageTypes.includes(f.type)) {
                this.storeImage(f);
            }
            if (this.validVideoTypes.includes(f.type)) {
                this.storeVideo(f);
            }
        });
    }

    storeImage(file: File) {
        this.uploading = true;
        this.imageService.storeImage(file, this.projectId).pipe(take(1)).subscribe();
    }

    storeVideo(file: File) {
        this.uploading = true;
        this.videoRecordingService.storeVideo(this.projectId, file, file.name).subscribe();
    }

    private thumbVideos = new Map<string, HTMLVideoElement>();

    onThumbLoaded(event: Event, id: string) {
        const video = event.target as HTMLVideoElement;
        video.currentTime = 0.1;
        this.thumbVideos.set(id, video);
    }

    onThumbTimeUpdate(event: Event) {
        const video = event.target as HTMLVideoElement;
        const duration = Math.min(isFinite(video.duration) ? video.duration : 10, 10);
        if (video.currentTime >= duration) {
            video.currentTime = 0;
        }
        const fill = video.parentElement?.querySelector('.data-entry-thumb-progress-fill') as HTMLElement | null;
        if (fill) {
            fill.style.width = `${(video.currentTime / duration) * 100}%`;
        }
    }

    onRowMouseEnter(id: string) {
        this.thumbVideos.get(id)?.play().catch(() => {});
    }

    onRowMouseLeave(id: string) {
        const video = this.thumbVideos.get(id);
        if (video) {
            video.pause();
            video.currentTime = 0.1;
            const fill = video.parentElement?.querySelector('.data-entry-thumb-progress-fill') as HTMLElement | null;
            if (fill) fill.style.width = '0%';
        }
    }

    onAnimationEnd(){
        this.animateSelectButton = false;
    }
    
    protected readonly externalLink = faExternalLink;
}
